import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, Plus } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/lib/seo";

const CATEGORIES = [
    {
        slug: "consulenza-fiscale",
        title: "Consulenza fiscale",
        sub: "Supporto nella gestione ordinaria degli aspetti fiscali e nella scelta delle soluzioni più adatte alla propria situazione.",
        services: [
            "Contabilità",
            "Dichiarazioni fiscali",
            "Bilanci e situazioni contabili",
            "Consulenza societaria",
            "Assistenza e pratiche fiscali",
        ],
    },
    {
        slug: "servizi-amministrativi",
        title: "Servizi amministrativi alle imprese",
        sub: "Supporto nella gestione delle attività amministrative, documentali e organizzative necessarie al regolare funzionamento dell'impresa.",
        services: [
            "Gestione documentale",
            "Segreteria e back office",
            "Fatturazione e supporto amministrativo",
        ],
    },
    {
        slug: "elaborazioni-contabili",
        title: "Elaborazioni contabili",
        sub: "Elaborazione, organizzazione e riepilogo dei dati contabili sulla base della documentazione fornita dall'impresa.",
        services: [
            "Elaborazione dei dati contabili",
            "Report e riepiloghi",
            "Supporto alla contabilità",
        ],
    },
    {
        slug: "finanza-agevolata",
        title: "Finanza agevolata e accesso al credito",
        sub: "Supporto nell'individuazione di opportunità quali bandi, contributi, incentivi o strumenti di finanza agevolata e nella presentazione della pratica.",
        services: [
            "Analisi delle opportunità",
            "Studio di fattibilità",
            "Accesso al credito",
            "Gestione pratiche",
        ],
    },
];

export default function Servizi() {
    useSEO(
        "Servizi | Service & Business SRLS",
        "Consulenza fiscale, servizi amministrativi, elaborazioni contabili e finanza agevolata: i servizi di Service & Business SRLS per imprese e professionisti, anche completamente da remoto."
    );

    const [open, setOpen] = useState(-1);

    return (
        <>
            <PageHero
                eyebrow="Servizi"
                lines={["Servizi", "per la gestione", "della tua attività."]}
                sub="Consulenza fiscale, servizi amministrativi, elaborazioni contabili e finanza agevolata: quattro aree di intervento per accompagnare la tua attività, anche completamente da remoto."
            />

            <section className="pb-8">
                <div className="wrap">
                    <div className="border-b border-white/8">
                        {CATEGORIES.map((c, i) => {
                            const isOpen = open === i;
                            return (
                                <Reveal key={c.slug} delay={i * 0.04}>
                                    <div className="border-t border-white/8" data-testid={`servizi-category-${c.slug}`}>
                                        <h2 className="sr-only">{c.title}</h2>
                                        <button
                                            type="button"
                                            data-testid={`servizi-toggle-${c.slug}`}
                                            aria-expanded={isOpen}
                                            onClick={() => setOpen(isOpen ? -1 : i)}
                                            className="w-full flex items-center gap-6 sm:gap-10 py-8 text-left group"
                                        >
                                            <span className="font-mono text-sm text-sky-400 w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                                            <span className="flex-1">
                                                <span className="block font-display text-xl sm:text-3xl font-bold text-white tracking-tight group-hover:text-sky-300 transition-colors duration-200">
                                                    {c.title}
                                                </span>
                                                <span className="block text-sm text-slate-400 leading-relaxed mt-2 max-w-2xl">
                                                    {c.sub}
                                                </span>
                                            </span>
                                            <span
                                                className={
                                                    "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border transition-all duration-300 " +
                                                    (isOpen ? "border-sky-400/60 bg-sky-400/10 rotate-45" : "border-white/20 group-hover:border-sky-400/50")
                                                }
                                                aria-hidden="true"
                                            >
                                                <Plus className={"h-5 w-5 transition-colors duration-300 " + (isOpen ? "text-sky-300" : "text-white/70")} />
                                            </span>
                                        </button>
                                        <AnimatePresence initial={false}>
                                            {isOpen && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                                    className="overflow-hidden"
                                                    data-testid={`servizi-panel-${c.slug}`}
                                                >
                                                    <div className="pb-10 pl-14 sm:pl-[4.5rem] pr-2">
                                                        <p className="font-mono text-[10px] tracking-[0.28em] text-white/40 uppercase mb-5">
                                                            Servizi inclusi
                                                        </p>
                                                        <ul className="grid sm:grid-cols-2 gap-3 max-w-3xl">
                                                            {c.services.map((s, j) => (
                                                                <li key={s} className="flex items-center gap-3 card-dark rounded-xl px-5 py-4" data-testid={`servizi-item-${c.slug}-${j + 1}`}>
                                                                    <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full" style={{ background: "linear-gradient(120deg,#2FB8EC,#0A5FC2)" }}>
                                                                        <Check className="h-3 w-3 text-white" />
                                                                    </span>
                                                                    <span className="text-sm text-slate-200">{s}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                        <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3">
                                                            <Link to="/contatti" data-testid={`servizi-cta-${c.slug}`} className="btn-ghost !px-5 !py-2.5 !text-[13px]">
                                                                Parla con noi
                                                                <ArrowRight className="h-4 w-4" />
                                                            </Link>
                                                            {c.slug === "finanza-agevolata" && (
                                                                <Link to="/bandi-finanza-agevolata" data-testid="servizi-link-bandi" className="inline-flex items-center gap-2 text-[13px] font-semibold text-sky-400 hover:text-sky-300 transition-colors">
                                                                    Approfondisci bandi e finanza agevolata
                                                                    <ArrowRight className="h-3.5 w-3.5" />
                                                                </Link>
                                                            )}
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="pb-4">
                <div className="wrap">
                    <Reveal>
                        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-surface px-8 py-14 sm:px-14 text-center">
                            <div
                                className="absolute -top-24 left-1/2 -translate-x-1/2 h-64 w-[36rem] rounded-full pointer-events-none"
                                style={{ background: "radial-gradient(closest-side, rgba(0,210,255,0.16), rgba(0,0,0,0))" }}
                            />
                            <h2 className="relative font-display text-2xl sm:text-4xl font-bold text-white tracking-tight">
                                Non sai quale servizio fa al caso tuo?
                            </h2>
                            <p className="relative mt-4 text-slate-400">Parlaci della tua esigenza.</p>
                            <div className="relative mt-9">
                                <Link to="/contatti" data-testid="servizi-final-cta" className="btn-primary">
                                    Parla con noi
                                    <ArrowRight className="h-4 w-4" />
                                </Link>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
